import { useState, useEffect } from 'react';
import { FormField, FormTab, FieldType } from '../../../types/field-builder';

interface FieldEditorProps {
  field: FormField;
  tabs: FormTab[];
  onSave: (field: FormField) => void;
  onClose: () => void;
}

const fieldTypes: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Texto Curto' },
  { value: 'textarea', label: 'Texto Longo' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Data' },
  { value: 'select', label: 'Seleção Única' },
  { value: 'multi_select', label: 'Seleção Múltipla' },
  { value: 'boolean', label: 'Sim/Não' },
];

export default function FieldEditor({ field, tabs, onSave, onClose }: FieldEditorProps) {
  const [formData, setFormData] = useState<FormField>(field);
  const [optionsText, setOptionsText] = useState(field.options?.join('\n') || '');

  const isNewField = !field.key.startsWith('field_');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.label.trim()) {
      alert('O rótulo é obrigatório');
      return;
    }

    if (!formData.key.trim()) {
      alert('A chave é obrigatória');
      return;
    }

    if (!formData.tab_id) {
      alert('Selecione uma aba');
      return;
    }

    // Processar opções para select/multi_select
    let finalField = { ...formData };
    if (formData.type === 'select' || formData.type === 'multi_select') {
      const options = optionsText
        .split('\n')
        .map(opt => opt.trim())
        .filter(opt => opt.length > 0);

      if (options.length === 0) {
        alert('Adicione pelo menos uma opção');
        return;
      }

      finalField.options = options;
    } else {
      delete finalField.options;
    }

    onSave(finalField);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {isNewField ? 'Novo Campo' : 'Editar Campo'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Rótulo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rótulo do Campo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Ex: Nome do Ativo"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Este é o texto que aparecerá no formulário
            </p>
          </div>

          {/* Chave */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chave Técnica <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
              placeholder="Ex: nome_ativo"
              required
              disabled={!isNewField}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isNewField 
                ? 'Identificador único (não pode ser alterado depois de criado)'
                : 'A chave não pode ser alterada após a criação'}
            </p>
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Campo <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as FieldType })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            >
              {fieldTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Aba */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Aba <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.tab_id}
              onChange={(e) => setFormData({ ...formData, tab_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            >
              <option value="">Selecione uma aba</option>
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.label}
                </option>
              ))}
            </select>
          </div>

          {/* Placeholder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Placeholder
            </label>
            <input
              type="text"
              value={formData.placeholder || ''}
              onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Ex: Digite aqui..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Texto de exemplo que aparece quando o campo está vazio
            </p>
          </div>

          {/* Texto de Ajuda */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Texto de Ajuda
            </label>
            <textarea
              value={formData.help_text || ''}
              onChange={(e) => setFormData({ ...formData, help_text: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              rows={2}
              placeholder="Informação adicional para ajudar o usuário"
            />
          </div>

          {/* Opções (para select/multi_select) */}
          {(formData.type === 'select' || formData.type === 'multi_select') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opções <span className="text-red-500">*</span>
              </label>
              <textarea
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
                rows={6}
                placeholder="Digite uma opção por linha&#10;Ex:&#10;Opção 1&#10;Opção 2&#10;Opção 3"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Digite uma opção por linha
              </p>
            </div>
          )}

          {/* Validações para número */}
          {formData.type === 'number' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Mínimo
                </label>
                <input
                  type="number"
                  value={formData.validation?.min || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    validation: { ...formData.validation, min: e.target.value ? Number(e.target.value) : undefined }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Ex: 0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Máximo
                </label>
                <input
                  type="number"
                  value={formData.validation?.max || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    validation: { ...formData.validation, max: e.target.value ? Number(e.target.value) : undefined }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Ex: 1000"
                />
              </div>
            </div>
          )}

          {/* Checkboxes */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Campo Obrigatório</span>
                <p className="text-xs text-gray-500">O usuário deve preencher este campo</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.readonly || false}
                onChange={(e) => setFormData({ ...formData, readonly: e.target.checked })}
                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Somente Leitura</span>
                <p className="text-xs text-gray-500">O campo não pode ser editado</p>
              </div>
            </label>
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 whitespace-nowrap cursor-pointer"
            >
              Salvar Campo
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
